import Box from '@mui/material/Box';
import { providerType } from '@repo/shared/types';
import { useLiberisContext } from '@repo/app-contexts/liberis';
import { MerchantDetails } from '@repo/api/endpoints/merchant';
import { useEffect } from 'react';

import { useMainHubStoreState } from '@/store/main-hub.store';
import { companyType } from '@/components/loan-form/schema/loan-form.schema';
import {
  defaultValuesJdg,
  defaultValuesOther,
} from '@/components/loan-offer-data-process/form/default-values';
import { getMinMaxLoanAmount } from '@/components/welcome/loan-application-possible/liberis/common/helpers/get-min-max-loan-amount';
import { LoanApplicationPossibleLiberisForm } from '@/components/loan-offer-data-process/form/loan-application-possible-liberis-form';
import { LiberisNoRepresentativeDataTitle } from '@/components/welcome/loan-application-possible/liberis/no-representative/liberis-no-representative-data-title';
import { LiberisNoBeneficientDataTitle } from '@/components/welcome/loan-application-possible/liberis/no-beneficient/liberis-no-beneficient-data-title';
import { CompleteDataContact } from '@/components/welcome/loan-application-possible/liberis/common/components/complete-data-contact';
import { WelcomeViewWrapperContextProvider } from '@/components/welcome/components/welcome-view-wrapper/context/welcome-view-wrapper-context-provider';
import { renderLoanApplicationPossibleLiberisStage } from '@/components/loan-offer-data-process/render-loan-application-possible-liberis-stage';
import { useModalStore } from '@/components/modal/modal.store';

const EmptyMerchantData = () => (
  <Box>
    <CompleteDataContact />
  </Box>
);

const NoRepresentativeData = () => (
  <Box>
    <LiberisNoRepresentativeDataTitle />
    <CompleteDataContact />
  </Box>
);

const NoBeneficientData = () => (
  <Box>
    <LiberisNoBeneficientDataTitle />
    <CompleteDataContact />
  </Box>
);

const getInitialValues = (
  amountInitialValue: number,
  merchantDetails: MerchantDetails | undefined,
  selectedCompanyType: string,
) => {
  if (!merchantDetails) return undefined;

  if (selectedCompanyType === 'jednoosobowa') {
    return defaultValuesJdg({
      amount: amountInitialValue,
      merchantDetails,
    });
  }

  return defaultValuesOther({
    amount: amountInitialValue,
    merchantDetails,
  });
};

export function MultiproviderLiberisStageModalContent() {
  const { liberisStage, setLiberisStage } = useLiberisContext();
  const { merchantResponseData } = useMainHubStoreState();
  const { additionalProps } = useModalStore();

  useEffect(() => {
    setLiberisStage('AGREEMENTS');
  }, []);

  if (!merchantResponseData) {
    return <EmptyMerchantData />;
  }

  const merchantDetails = merchantResponseData?.data?.merchantData;

  if (merchantDetails && merchantDetails.representative.length === 0) {
    return <NoRepresentativeData />;
  }

  const selectedCompanyType = companyType(merchantDetails?.companyType || 'jednoosobowa');
  const isJdg = selectedCompanyType === 'jednoosobowa';

  if (!isJdg && merchantDetails && merchantDetails.beneficiary.length === 0) {
    return <NoBeneficientData />;
  }

  const loanAmountDefaultValuesLiberis = getMinMaxLoanAmount(
    merchantResponseData,
    providerType.enum.liberis,
  );

  const amountInitialValue = loanAmountDefaultValuesLiberis.max;

  const initialValues = getInitialValues(amountInitialValue, merchantDetails, selectedCompanyType);
  if (!initialValues) return null;

  const stageData = renderLoanApplicationPossibleLiberisStage(liberisStage, initialValues, {
    withWrapper: false,
    maxLiberisAmount:
      typeof additionalProps.maxLiberisAmount === 'number'
        ? additionalProps.maxLiberisAmount
        : undefined,
  });

  if (!stageData) return null;

  const { buttonText, onButtonClick, onSubmit, component, withMarketingAgreementForm, withSubmit } =
    stageData;

  return (
    <WelcomeViewWrapperContextProvider>
      <LoanApplicationPossibleLiberisForm
        buttonText={buttonText}
        onButtonClick={onButtonClick}
        onSubmit={onSubmit}
        withMarketingAgreementForm={withMarketingAgreementForm}
        withSubmit={withSubmit}
        initialValues={initialValues}
      >
        {component}
      </LoanApplicationPossibleLiberisForm>
    </WelcomeViewWrapperContextProvider>
  );
}
